import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImagePicker from "@/components/admin/ImagePicker";

const EMPTY = { name: "", address: "", phone: "", hours: "", image: "", info: "", active: true, order: 0 };

export default function PickupAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/pickup-locations").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/pickup-locations/${editing.id}`, payload);
      else await api.post("/admin/pickup-locations", payload);
      toast.success("Point de retrait enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce point de retrait ?")) return;
    await api.delete(`/admin/pickup-locations/${id}`);
    toast.success("Supprimé");
    load();
  };

  const toggle = async (item) => {
    await api.put(`/admin/pickup-locations/${item.id}`, { ...item, active: !item.active });
    load();
  };

  return (
    <div data-testid="admin-pickup">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Points de retrait</h1>
          <p className="text-sm text-muted-foreground mt-1">Lieux de retrait gratuit des commandes en ligne (≠ points de vente).</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="pickup-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {!items ? <p className="text-muted-foreground">Chargement…</p> : (
        <div className="space-y-3" data-testid="pickup-list">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`pickup-row-${p.id}`}>
              <PackageCheck className="w-5 h-5 text-rouge shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="font-display font-bold text-onyx">{p.name}</p>
                <p className="text-sm text-muted-foreground">{p.address}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!p.active} onCheckedChange={() => toggle(p)} data-testid={`pickup-active-${p.id}`} />
                <span className="text-xs text-muted-foreground">{p.active ? "Actif" : "Inactif"}</span>
              </div>
              <button onClick={() => setEditing({ ...p })} data-testid={`pickup-edit-${p.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`pickup-delete-${p.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white" data-testid="pickup-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Ajouter"} un point de retrait</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nom</Label><Input data-testid="pickup-name-input" className="mt-1.5" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Adresse</Label><Input data-testid="pickup-address-input" className="mt-1.5" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Téléphone</Label><Input data-testid="pickup-phone-input" className="mt-1.5" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
                <div><Label>Horaires</Label><Input data-testid="pickup-hours-input" className="mt-1.5" value={editing.hours || ""} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} /></div>
              </div>
              <ImagePicker label="Photo / logo (optionnel)" value={editing.image || ""} onChange={(url) => setEditing({ ...editing, image: url })} />
              <div><Label>Informations pratiques</Label><Input data-testid="pickup-info-input" className="mt-1.5" value={editing.info} onChange={(e) => setEditing({ ...editing, info: e.target.value })} /></div>
              <div><Label>Ordre d'affichage</Label><Input type="number" data-testid="pickup-order-input" className="mt-1.5" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <div className="flex items-center gap-3">
                <Switch data-testid="pickup-active-switch" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Actif</Label>
              </div>
              <button onClick={save} disabled={saving || !editing.name} data-testid="pickup-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
